import React, { useState, useEffect, useCallback } from 'react';
import { CreditCard, Loader2, CheckCircle2, Receipt, ShieldCheck } from 'lucide-react';
import { carsysApi } from '../api/carsysApi';
import { useT } from '../context/LanguageContext';

const eur = (n) => (Number(n) || 0).toLocaleString('nl-NL', { style: 'currency', currency: 'EUR' });
const getParam = (name) => new URLSearchParams(window.location.search).get(name);

export default function BetaalPage({ token }) {
  const { t } = useT();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [polling, setPolling] = useState(false);
  const [paid, setPaid] = useState(false);

  const load = useCallback(() => {
    carsysApi.getPublicFactuur(token)
      .then(d => { setData(d); if (d.isBetaald) setPaid(true); })
      .catch(e => setError(e.response?.status === 404 ? 'notfound' : 'error'));
  }, [token]);

  const pollStatus = useCallback(async (sessionId, attempt = 0) => {
    if (attempt >= 8) { setPolling(false); load(); return; }
    try {
      const res = await carsysApi.publicFactuurStatus(sessionId);
      if (res.payment_status === 'paid') { setPaid(true); setPolling(false); return; }
    } catch (e) { /* keep trying */ }
    setTimeout(() => pollStatus(sessionId, attempt + 1), 2000);
  }, [load]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const sid = getParam('session_id');
    if (sid) { setPolling(true); pollStatus(sid); }
  }, [pollStatus]);

  const betaal = async () => {
    setBusy(true);
    try {
      const res = await carsysApi.publicFactuurCheckout(token, window.location.origin);
      if (res.url) window.location.href = res.url;
      else throw new Error('Geen betaal-URL ontvangen');
    } catch (e) {
      setError('error');
      setBusy(false);
    }
  };

  if (error === 'notfound') return <Center>🔍 {t('Factuur niet gevonden of de link is verlopen.', 'Factuur niet gevonden of de link is verlopen.')}</Center>;
  if (error) return <Center>{t('Er ging iets mis. Probeer het later opnieuw.', 'Er ging iets mis. Probeer het later opnieuw.')}</Center>;
  if (!data) return <Center><Loader2 className="spin" size={28} /></Center>;

  return (
    <div style={{ minHeight: '100vh', background: '#f1f5f9', fontFamily: 'system-ui, sans-serif', padding: '24px 12px' }} data-testid="betaal-page">
      <div style={{ maxWidth: 460, margin: '0 auto' }}>
        <div style={{ background: '#0f172a', color: '#fff', borderRadius: '14px 14px 0 0', padding: '20px 24px' }}>
          <div style={{ fontSize: 13, opacity: .8 }}>{data.garage.naam}</div>
          <div style={{ fontSize: 22, fontWeight: 800, display: 'flex', alignItems: 'center', gap: 8 }}>
            <Receipt size={20} /> {t('Factuur', 'Factuur')} {data.nummer}
          </div>
          <div style={{ fontSize: 13, opacity: .85, marginTop: 4 }}>{data.kenteken ? `${data.kenteken} · ` : ''}{data.datum}</div>
        </div>

        <div style={{ background: '#fff', padding: 24 }}>
          {data.omschrijving && <p style={{ marginTop: 0, color: '#334155', fontSize: 14 }}>{data.omschrijving}</p>}
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13.5, color: '#64748b', padding: '4px 0' }}>
            <span>{t('Totaal', 'Totaal')}</span><span>{eur(data.totaal)}</span>
          </div>
          {data.betaald > 0 && (
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13.5, color: '#15803d', padding: '4px 0' }}>
              <span>{t('Reeds betaald', 'Reeds betaald')}</span><span>{eur(data.betaald)}</span>
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 18, fontWeight: 800, padding: '10px 0 0', borderTop: '1px solid #eef2f7', marginTop: 8 }}>
            <span>{t('Te betalen', 'Te betalen')}</span><span data-testid="betaal-bedrag">{eur(data.openstaand)}</span>
          </div>
        </div>

        <div style={{ background: '#fff', borderTop: '1px solid #e2e8f0', borderRadius: '0 0 14px 14px', padding: 24 }}>
          {paid ? (
            <div style={{ textAlign: 'center', color: '#15803d' }} data-testid="betaal-bedankt">
              <CheckCircle2 size={44} style={{ marginBottom: 8 }} />
              <div style={{ fontSize: 18, fontWeight: 800 }}>{t('Betaling geslaagd!', 'Betaling geslaagd!')}</div>
              <div style={{ fontSize: 13.5, color: '#475569', marginTop: 4 }}>{t('Bedankt voor uw betaling.', 'Bedankt voor uw betaling.')}</div>
            </div>
          ) : polling ? (
            <div style={{ textAlign: 'center', color: '#64748b' }} data-testid="betaal-polling">
              <Loader2 className="spin" size={32} style={{ marginBottom: 8 }} />
              <div style={{ fontSize: 14, fontWeight: 600 }}>{t('Betaling wordt verwerkt…', 'Betaling wordt verwerkt…')}</div>
            </div>
          ) : (
            <>
              <button onClick={betaal} disabled={busy || data.openstaand <= 0} data-testid="betaal-knop"
                style={{ width: '100%', padding: '14px', border: 'none', borderRadius: 10, background: '#2563eb', color: '#fff', fontSize: 15, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                {busy ? <Loader2 className="spin" size={16} /> : <CreditCard size={16} />}
                {t('Betaal nu online', 'Betaal nu online')} · {eur(data.openstaand)}
              </button>
              <p style={{ fontSize: 11.5, color: '#94a3b8', marginTop: 12, textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5 }}>
                <ShieldCheck size={13} /> {t('Veilig betalen met iDEAL of creditcard via Stripe', 'Veilig betalen met iDEAL of creditcard via Stripe')}
              </p>
            </>
          )}
        </div>
        <div style={{ textAlign: 'center', fontSize: 11, color: '#94a3b8', marginTop: 14 }}>{t('Mogelijk gemaakt door', 'Mogelijk gemaakt door')} MonteurCentraal</div>
      </div>
      <style>{`.spin{animation:spin 1s linear infinite}@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}

function Center({ children }) {
  return <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f1f5f9', color: '#64748b', fontFamily: 'system-ui', fontSize: 16, padding: 20, textAlign: 'center' }}>{children}</div>;
}
